import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { Dialog } from "components/common";
import { Input } from "components/common/input/Input";
import { Form } from "components/common/form/Form";

import { usStates } from "constants/us_states";
import { getDefaultInputValue } from "utility/form";
import { renderConditionalProp } from "utility/utils";
import {
  cityRegex,
  emailRegex,
  genericRegex,
  phoneRegex,
  zipRegex,
} from "utility/regex";
import { scopes } from "constants/idp-scopes";

import { useRequestDispatch } from "store/requestHooks";
import { useInput, UseInput } from "hooks/useInput";
import { useRootDispatch, useRootSelector } from "store/hooks";

import {
  AuthSession,
  Notifications,
  Preferences,
  UserAccount,
} from "store/user/user";
import { resetUser, setUser } from "store/user/user-slice";
import { clearSlide } from "store/common/common-slice";
import { selectedAccountInfo } from "store/account/account-selector";
import {
  AttachAccount,
  CreateUserAccount,
  UpdateUserAccount,
  attachAccount,
  createUserAccount,
  updateUserAccount,
} from "store/user/user-action";
import { AccountQueryParameter } from "store/account/account-state";

import classes from "./Create.module.scss";
import { useAssets } from "hooks";

interface CreateProps {
  editMode?: boolean;
  editHandler?: Function;
}

export const Create: React.FC<CreateProps> = ({ editMode, editHandler }) => {
  const { t } = useTranslation(["account", "account_next", "common", "app"]);
  const navigate = useNavigate();
  const dispatch = useRootDispatch();
  const requestDispatch = useRequestDispatch(
    editMode ? scopes.users.writeUser : "",
  );

  const { loginWithRedirect, logout } = useAuth0();
  const [searchParams] = useSearchParams();

  const { createTermsAndConditionsUrl } = useAssets();

  const session_token = searchParams.get("session_token") || "";
  const state = searchParams.get("state") || "";

  const { client, paymentCenter, applicationName, notifications } =
    useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const { user, meta } = useRootSelector((state) => state.user);

  const [formState, setFormState] = useState<any>({});
  const [states] = useState<any>(usStates);
  const [showDialog, setShowDialog] = useState(false);
  const [isRedirectOn, setIsRedirectOn] = useState(false);
  const [isLogoutRequired, setIsLogoutRequired] = useState(false);

  const eStatementEnableFlag = !!(
    user?.preferences.notifications.templatePreferences?.eStatementAvailable
      ?.sendEmail ||
    user?.preferences.notifications.templatePreferences?.eStatementAvailable
      ?.sendSms
  );
  const [disableFlag, setDisableFlag] = useState(eStatementEnableFlag);

  const tnc: UseInput = useInput(
    getDefaultInputValue(user && user?.tnc ? user.tnc : false),
  );
  const mfa: UseInput = useInput(
    getDefaultInputValue(user && user?.requireMFA ? user.requireMFA : false),
  );

  const eStatementEmail: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.eStatementAvailable
        ?.sendEmail,
    ),
  );

  const eStatementText: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.eStatementAvailable
        ?.sendSms,
    ),
  );

  //#region Notification Settings Input fields
  const prEmail: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.paymentReminder
        ?.sendEmail,
    ),
  );

  const prText: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.paymentReminder
        ?.sendSms,
    ),
  );

  const pdrEmail: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.pastDueReminder
        ?.sendEmail,
    ),
  );

  const pdrText: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.pastDueReminder
        ?.sendSms,
    ),
  );

  const pcEmail: UseInput = useInput(getDefaultInputValue(true));

  const pcText: UseInput = useInput(
    getDefaultInputValue(
      !!user?.preferences.notifications.templatePreferences?.paymentConfirmation
        ?.sendSms,
    ),
  );

  /*   const country =
    formState && formState.country && formState.country.value
      ? formState.country.value
      : null; */

  useEffect(() => {
    if (!state && !editMode) {
      navigate(`/${client.customerKey}`);
    }
  }, [client.customerKey, navigate, state, editMode]);

  const setFormData = useCallback((key: string, val: any) => {
    setFormState((state: any) => {
      return {
        ...state,
        [key]: val,
      };
    });
  }, []);

  /* const fetchAndUpdateState = useCallback(
    (country: string) => {
      let file = "us_states.json";
      if (country === "canada") {
        file = "canada_states.json";
      }
      fetch(`${import.meta.env.BASE_URL}data/${file}`)
        .then((r) => r.json())
        .then((data) => {
          setStates(data);
          dispatch(
            setIsLoading({
              isLoading: false,
              fetchingData: false,
            })
          );
        });
    },
    [dispatch]
  ); */

  /*   useEffect(() => {
    if (country) {
      fetchAndUpdateState(country);
    }
  }, [country, fetchAndUpdateState]); */

  const toggleHandler = () => {
    if (disableFlag) {
      eStatementEmail.value = false;
      eStatementText.value = false;
    } else {
      eStatementEmail.value = true;
    }
    setDisableFlag(!disableFlag);
  };

  const submithandler = () => {
    let formIsValid = false;

    if (
      formState.firstName.ref.current.checkValidity() &&
      formState.lastName.ref.current.checkValidity() &&
      formState.addressLine1.ref.current.checkValidity() &&
      formState.country.ref.current.checkValidity() &&
      formState.city.ref.current.checkValidity() &&
      formState.state.ref.current.checkValidity() &&
      formState.zip.ref.current.checkValidity() &&
      formState.phone.ref.current.checkValidity() &&
      formState.textPhoneNumber.ref.current.checkValidity()
    ) {
      formIsValid = true;
    } else {
      formState.firstName.ref.current.reportValidity();
      formState.lastName.ref.current.reportValidity();
      formState.addressLine1.ref.current.reportValidity();
      formState.country.ref.current.reportValidity();
      formState.city.ref.current.reportValidity();
      formState.state.ref.current.reportValidity();
      formState.zip.ref.current.reportValidity();
      formState.phone.ref.current.reportValidity();
      formState.textPhoneNumber.ref.current.reportValidity();
    }

    if (editMode && !formState.email.ref.current.checkValidity()) {
      formState.email.ref.current.reportValidity();
      formIsValid = false;
    }

    // Check if the registration tracking properties present in the state are valid, if not valid then report it & set form as invalid
    for (const single in formState) {
      if (!formState[single].ref.current.checkValidity()) {
        formState[single].ref.current.reportValidity();
        formIsValid = false;
      }
    }

    if (!editMode && !tnc.value) {
      formIsValid = false;
    }

    if (disableFlag && paymentCenter.statementsEnabled) {
      if (!(eStatementEmail.value || eStatementText.value)) {
        formIsValid = false;
      }
    }

    if (formIsValid) {
      const useMfa =
        client.mfaUserPermissions === 1 ||
        (client.mfaUserPermissions === 2 && mfa.value);

      const notifications: Notifications = {
        ...user?.preferences?.notifications,
        templatePreferences: {
          ...user?.preferences?.notifications.templatePreferences,
          pastDueReminder: {
            sendEmail: pdrEmail.value ? true : false,
            sendSms: pdrText.value ? true : false,
          },
          paymentReminder: {
            sendEmail: prEmail.value ? true : false,
            sendSms: prText.value ? true : false,
          },
          paymentConfirmation: {
            sendEmail: pcEmail.value ? true : false,
            sendSms: pcText.value ? true : false,
          },
          /* eStatementAvailable: {
            sendEmail: eStatementEmail.value ? true : false,
            sendSms: eStatementText.value ? true : false,
          }, */
        },
        textPhoneNumber: formState.textPhoneNumber.value,
      };

      const preferences: Preferences = {
        ...user?.preferences,
        notifications,
      };

      const requestData: Partial<UserAccount> = {
        firstName: formState.firstName.value,
        givenName: formState.firstName.value,
        lastName: formState.lastName.value,
        familyName: formState.lastName.value,
        addressLine1: formState.addressLine1.value,
        addressLine2: formState.addressLine2.value,
        country: formState.country.value,
        city: formState.city.value,
        state: formState.state.value,
        postalCode: formState.zip.value,
        companyName: formState.companyName.value,
        phone: formState.phone.value,
        termsAcknowledgement: new Date().toISOString(),
        requireMFA: useMfa,
        preferences,
        email: formState?.email?.value
          ? formState.email.value.replace(/-/g, "")
          : "",
      };

      if (!editMode) {
        const accountOwnershipIdentifiers: AccountQueryParameter[] =
          paymentCenter.registrationTrackingProperties.map((single) => {
            if (single.propertyName.toUpperCase() === "Zip".toUpperCase()) {
              return {
                trackingProperty: single,
                value: formState && formState["zip"].value,
              };
            } else {
              return {
                trackingProperty: single,
                value:
                  (formState && formState[single.propertyName]?.value) || null,
              };
            }
          });
        requestData.accountOwnershipIdentifiers = accountOwnershipIdentifiers;
      }

      processAccountAttachProperties(
        requestData as UserAccount,
        user?.email !== formState?.email?.value,
      );
    }
  };

  const processAccountAttachProperties = (
    requestData: UserAccount,
    updateEmail: boolean = false,
  ) => {
    if (client.clientId) {
      if (editMode) {
        requestDispatch<UpdateUserAccount, UserAccount>(
          updateUserAccount,
          requestData as UserAccount,
        )
          .unwrap()
          .then(() => {
            if (updateEmail) {
              setIsLogoutRequired(true);
            }
            setShowDialog(true);
          })
          .catch((e: any) => {
            console.log(e);
          });
      } else {
        dispatch(
          setUser({
            ...requestData,
          }),
        );
        requestDispatch<
          AttachAccount,
          {
            accountOwnershipIdentifiers: AccountQueryParameter[];
            authSession: AuthSession;
          }
        >(attachAccount, {
          accountOwnershipIdentifiers: requestData.accountOwnershipIdentifiers!,
          authSession: { session_token, state },
        })
          .unwrap()
          .then(() => {
            requestDispatch<
              CreateUserAccount,
              {
                user: UserAccount;
                authSession: AuthSession;
              }
            >(createUserAccount, {
              user: requestData,
              authSession: { session_token, state },
            })
              .unwrap()
              .then((response: string) => {
                setIsRedirectOn(true);
                const uri = new URL(
                  `https://${import.meta.env.REACT_APP_AUTH0_DOMAIN}/continue`,
                );
                uri.searchParams.append("session_token", response);
                if (!!state) {
                  uri.searchParams.append("state", state);
                }
                dispatch(resetUser());
                window.location.assign(uri.toString());
              })
              .catch((err: AxiosError) => {
                auth0loginRedirect(err);
              });
          })
          .catch((err: AxiosError<any, any>) => {
            auth0loginRedirect(err);
          });
      }
    }
  };

  const auth0loginRedirect = (err: AxiosError<any, any>) => {
    if (err?.response?.status === 401) {
      setIsRedirectOn(true);
      loginWithRedirect();
    }
  };

  const dialogAction = () => {
    setShowDialog(false);
    dispatch(clearSlide());
    if (isLogoutRequired) {
      dispatch(resetUser());
      logout({
        logoutParams: {
          returnTo: `${window.location.origin}/${client.customerKey}`,
        },
      });
    }
  };

  const renderFields = () => {
    return (
      <>
        <div key={"div_firstName"} className={classes.Form_Field}>
          <Input
            id={"firstName"}
            datacy="account-page-firstName"
            passState={setFormData}
            label={t("account_next:first_name_label")}
            val={user?.givenName ? user.givenName : user?.firstName || ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ required: true, pattern: genericRegex, maxLength: 50 }}
          />
        </div>
        <div key={"div_lastName"} className={classes.Form_Field}>
          <Input
            id={"lastName"}
            datacy="account-page-lastName"
            passState={setFormData}
            label={t("account_next:last_name_label")}
            val={user?.familyName ? user.familyName : user?.lastName || ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ required: true, pattern: genericRegex, maxLength: 50 }}
          />
        </div>
        <div key={"div_addressLine1"} className={classes.Form_Field}>
          <Input
            id={"addressLine1"}
            datacy="account-page-addressLine1"
            passState={setFormData}
            label={t("account_next:address_line1")}
            val={user?.addressLine1 ? user.addressLine1 : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ required: true, maxLength: 50 }}
          />
        </div>
        <div key={"div_addressLine2"} className={classes.Form_Field}>
          <Input
            id={"addressLine2"}
            datacy="account-page-addressLine2"
            passState={setFormData}
            label={t("account_next:address_line2")}
            val={user?.addressLine2 ? user.addressLine2 : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ maxLength: 50 }}
          />
        </div>
        <div key={"div_country"} className={classes.Form_Field}>
          <Input
            id={"country"}
            datacy="account-page-country"
            passState={setFormData}
            label={t("account_next:country")}
            val={user?.country ? user.country : "United States"}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ required: true }}
            type={"select"}
            options={[
              { val: "United States", label: "United States" },
              /* { val: "Canada", label: "Canada" }, */
            ]}
          />
        </div>
        <div key={"div_city"} className={classes.Form_Field}>
          <Input
            id={"city"}
            datacy="account-page-city"
            passState={setFormData}
            label={t("account_next:city")}
            val={user?.city ? user.city : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{
              required: true,
              pattern: cityRegex,
            }}
          />
        </div>
        <div key={"div_state"} className={classes.Form_Field}>
          <Input
            id={"state"}
            datacy="account-page-state"
            passState={setFormData}
            label={t("account_next:state")}
            val={user?.state ? user.state : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{ required: true }}
            type={"select"}
            options={states}
          />
        </div>
        <div key={"div_zip"} className={classes.Form_Field}>
          <Input
            id={"zip"}
            datacy="account-page-zip"
            passState={setFormData}
            label={t("account_next:postalCode")}
            val={user?.postalCode ? user.postalCode : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{
              required: true,
              pattern: zipRegex,
            }}
            type="text"
          />
        </div>
        <div key={"div_phone"} className={classes.Form_Field}>
          <Input
            id={"phone"}
            datacy="account-page-phone"
            passState={setFormData}
            label={t("account_next:phone_home_business")}
            val={user?.phone ? user.phone : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{
              pattern: phoneRegex,
            }}
            type={"text"}
          />
        </div>
        <div key={"div_textPhoneNumber"} className={classes.Form_Field}>
          <Input
            id={"textPhoneNumber"}
            datacy="account-page-textPhoneNumber"
            passState={setFormData}
            label={t("account_next:cell_phone")}
            val={
              user?.preferences?.notifications?.textPhoneNumber
                ? user.preferences.notifications.textPhoneNumber
                : ""
            }
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{
              ...renderConditionalProp(
                pcText.value || pdrText.value || prText.value,
                "required",
              ),
              pattern: phoneRegex,
            }}
            type={"text"}
          />
        </div>
        {paymentCenter.registrationTrackingProperties.length > 0 &&
          !editMode && (
            <Form
              fields={paymentCenter.registrationTrackingProperties.filter(
                (rtp) => rtp.propertyName.toUpperCase() !== "Zip".toUpperCase(),
              )}
              genericValidation={true}
              values={accountInfo}
              formStateSetter={setFormState}
              layoutClass={
                editMode ? ["Form_Col_Field_Edit"] : ["Form_Col_Field"]
              }
            />
          )}
        {editMode ? (
          <div key={"div_email"} className={classes.Form_Field}>
            <Input
              id={"email"}
              datacy="account-page-email"
              passState={setFormData}
              label={t("account_next:email_label")}
              val={user?.email ? user.email : ""}
              validationMessage={t("common:invalid_field_generic")}
              extraProp={{
                required: true,
                pattern: emailRegex,
              }}
            />
            <p className={classes.Email_Text}>
              *{t("account_next:account_email_text")}
            </p>
          </div>
        ) : null}
        <div key={"div_company_name"} className={classes.Form_Field}>
          <Input
            id={"companyName"}
            data-testid="account-page-companyName"
            passState={setFormData}
            label={t("account_next:company_name_label")}
            val={user?.companyName ? user.companyName : ""}
            validationMessage={t("common:invalid_field_generic")}
            extraProp={{
              maxLength: 100,
            }}
            type="text"
          />
        </div>
      </>
    );
  };

  const renderEStatementControls = () => {
    return (
      <div className={classes.EStatements}>
        <ft-divider></ft-divider>
        <h3>{t("account_next:eStatement_control_title")}</h3>
        <div key={"eStatements_toggle"} className={classes.Fields}>
          <div className={classes.ToggleContainer}>
            <h5>
              {t("account_next:eStatements")} (
              {disableFlag
                ? t("account_next:enrolled")
                : t("account_next:notenrolled")}
              )
            </h5>
            <ft-switch
              data-testid="account-page-eStatement-enroll-email"
              aria-label="eStatements switch button"
              onClick={toggleHandler}
              {...renderConditionalProp(disableFlag, "selected")}
            ></ft-switch>
          </div>
          <p className={classes.Text12}>
            {t("account_next:eStatement_enroll_text")}
          </p>
        </div>
        <div key={"eStatements_checkbox"} className={classes.Fields}>
          <h5>{t("account_next:eStatement_available")}</h5>
          <ft-formfield label={t("account_next:email_label")}>
            <ft-checkbox
              data-testid="account-page-eStatement-enroll-email"
              onClick={
                disableFlag ? eStatementEmail.valueCheckedHandler : undefined
              }
              {...renderConditionalProp(eStatementEmail.value, "checked")}
              {...renderConditionalProp(!disableFlag, "disabled")}
            ></ft-checkbox>
          </ft-formfield>
          <ft-formfield label={t("account_next:text_sms")}>
            <ft-checkbox
              data-testid="account-page-eStatement-enroll-text"
              onClick={
                disableFlag ? eStatementText.valueCheckedHandler : undefined
              }
              {...renderConditionalProp(eStatementText.value, "checked")}
              {...renderConditionalProp(!disableFlag, "disabled")}
            ></ft-checkbox>
          </ft-formfield>
          <p className={classes.EStatementError}>
            {disableFlag && !(eStatementEmail.value || eStatementText.value)
              ? t("account_next:eStatement_enroll_error")
              : " "}
          </p>
        </div>
      </div>
    );
  };

  const renderNotificationFields = () => {
    return (
      <div className={classes.Notification}>
        <ft-divider></ft-divider>
        <h3>{t("account_next:notification_configurations")}</h3>
        {paymentCenter.notifications.sendPaymentReminder ? (
          <div key={"payment_reminder_scheduled"} className={classes.Fields}>
            <h5>
              {t("account_next:payment_reminder_scheduled", {
                day: paymentCenter.notifications
                  .daysBeforeDueDateToSendPaymentDueReminder,
              })}
            </h5>
            {notifications.enableEmail && (
              <ft-formfield label={t("account_next:email_label")}>
                <ft-checkbox
                  data-testid="account-page-payment-reminder-email"
                  onClick={prEmail.valueCheckedHandler}
                  {...renderConditionalProp(prEmail.value, "checked")}
                ></ft-checkbox>
              </ft-formfield>
            )}
            {notifications.enableSMS && (
              <ft-formfield label={t("account_next:text_sms")}>
                <ft-checkbox
                  data-testid="account-page-payment-reminder-sms"
                  onClick={prText.valueCheckedHandler}
                  {...renderConditionalProp(prText.value, "checked")}
                ></ft-checkbox>
              </ft-formfield>
            )}
          </div>
        ) : null}
        {paymentCenter.notifications.sendPastDue ? (
          <div key={"payment_past_due"} className={classes.Fields}>
            <h5>
              {t("account_next:payment_past_due", {
                day: paymentCenter.notifications.daysAfterDueDateToSendPastDue,
              })}
            </h5>
            {notifications.enableEmail && (
              <ft-formfield label={t("account_next:email_label")}>
                <ft-checkbox
                  data-testid="account-page-past-due-email"
                  onClick={pdrEmail.valueCheckedHandler}
                  {...renderConditionalProp(pdrEmail.value, "checked")}
                ></ft-checkbox>
              </ft-formfield>
            )}
            {notifications.enableSMS && (
              <ft-formfield label={t("account_next:text_sms")}>
                <ft-checkbox
                  data-testid="account-page-past-due-sms"
                  onClick={pdrText.valueCheckedHandler}
                  {...renderConditionalProp(pdrText.value, "checked")}
                ></ft-checkbox>
              </ft-formfield>
            )}
          </div>
        ) : null}
        <div key={"payment_confirmation"} className={classes.Fields}>
          <h5>{t("account_next:payment_confirmation")}</h5>
          <ft-formfield label={t("account_next:email_label")}>
            <ft-checkbox
              data-testid="account-page-payment-confirmation-sms"
              {...renderConditionalProp(pcEmail.value, "checked")}
              disabled
            ></ft-checkbox>
          </ft-formfield>
          {notifications.enableSMS && (
            <ft-formfield label={t("account_next:text_sms")}>
              <ft-checkbox
                data-testid="account-page-payment-confirmation-sms"
                onClick={pcText.valueCheckedHandler}
                {...renderConditionalProp(pcText.value, "checked")}
              ></ft-checkbox>
            </ft-formfield>
          )}
        </div>
      </div>
    );
  };

  const render2FactorForm = () => {
    return (
      <div className={classes.TwofecWrapper}>
        <ft-divider></ft-divider>
        <h3 className={classes.Mfa_Heading}>{t("account_next:mfa_heading")}</h3>
        <p className={classes.Text12}>{t("account_next:mfa_desc")}</p>
        <div className={classes.Mfa_radio}>
          <ft-formfield
            class={classes.Mfa_radio_field}
            label={t("common:yes_label")}
          >
            <ft-radio
              data-testid="account-page-mfa-yes"
              onClick={mfa.valueChangeHandler}
              {...renderConditionalProp(mfa.value, "checked")}
              value={true}
            ></ft-radio>
          </ft-formfield>
          <ft-formfield
            class={classes.Mfa_radio_field}
            label={t("common:no_label")}
          >
            <ft-radio
              data-testid="account-page-mfa-no"
              onClick={mfa.valueChangeHandler}
              {...renderConditionalProp(!mfa.value, "checked")}
              value={false}
            ></ft-radio>
          </ft-formfield>
        </div>
      </div>
    );
  };

  const buildBottomForm = () => {
    return (
      <>
        {paymentCenter.statementsEnabled && renderEStatementControls()}
        <div className={classes.SettingsWrapper}>
          {paymentCenter.notifications.sendNotifications &&
            (notifications.enableEmail || notifications.enableSMS) &&
            renderNotificationFields()}
          {!!(client.mfaUserPermissions === 2 && !editMode) &&
            render2FactorForm()}
        </div>
        {!editMode && (
          <div className={classes.TncWrapper}>
            <ft-divider></ft-divider>
            <ft-formfield
              class={classes.Tnc}
              label={t("account_next:account_terms_and_conditions")}
            >
              <ft-checkbox
                data-testid="account-page-tnc"
                onClick={tnc.valueCheckedHandler}
                {...renderConditionalProp(tnc.value, "checked")}
              ></ft-checkbox>
            </ft-formfield>
            {!tnc.value ? (
              <p className={classes.TncError}>
                {t("account_next:terms_and_condition_error")}
              </p>
            ) : null}
            <p>
              <a
                className={classes.TncLink}
                href={createTermsAndConditionsUrl()}
                target="_blank"
                rel="noreferrer"
              >
                Download
              </a>
            </p>
          </div>
        )}
        <div className={classes.FormButtons}>
          {editMode && (
            <ft-button
              label={t("common:back_label")}
              data-testid="account-page-back"
              outlined
              onClick={() => editHandler && editHandler()}
            ></ft-button>
          )}
          <ft-button
            class={editMode ? classes.SaveButton : ""}
            data-testid="account-page-save"
            label={editMode ? t("common:save_label") : t("common:next_label")}
            unelevated
            {...renderConditionalProp(!editMode && !tnc.value, "disabled")}
            onClick={submithandler}
          ></ft-button>
        </div>
      </>
    );
  };

  return (
    <>
      {meta.isLoading || isRedirectOn ? (
        <ft-screen-loader></ft-screen-loader>
      ) : null}
      <div
        className={[classes.Container, editMode ? classes.EditMode : ""].join(
          " ",
        )}
      >
        {!editMode ? (
          <>
            <p className={classes.Heading}>{t("app:create_account_label")}</p>
            <p className={classes.PageText}>
              {t("account_next:create_account_detail", {
                clientName: applicationName,
              })}
            </p>
          </>
        ) : (
          <p className={classes.EditPageText}>
            {t("account_next:account_update_detail", {
              clientName: applicationName,
            })}
          </p>
        )}
        <div className={classes.Form}>{renderFields()}</div>
        <div className={classes.FormBottom}>{buildBottomForm()}</div>
        {showDialog ? (
          <Dialog
            icon="account_balance"
            dialogClosed={dialogAction}
            primaryActionLabel={t("common:done")}
            primaryActionHandler={dialogAction}
            type="default"
          >
            {t("account_next:account_update_successful")}
          </Dialog>
        ) : null}
      </div>
    </>
  );
};
